import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ProductCard from './ProductCard'

function ProductGrid({
  products = [],
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onProductClick,
  loading = false,
}) {
  const { t } = useTranslation('ecommerce')

  const handleSelectAll = useCallback(() => {
    if (selectedIds.length === products.length) {
      onSelectionChange?.([])
    } else {
      onSelectionChange?.(products.map((p) => p.product_id))
    }
  }, [products, selectedIds, onSelectionChange])

  const handleSelectProduct = useCallback(
    (productId, checked) => {
      if (checked) {
        onSelectionChange?.([...selectedIds, productId])
      } else {
        onSelectionChange?.(selectedIds.filter((id) => id !== productId))
      }
    },
    [selectedIds, onSelectionChange]
  )

  const allSelected = products.length > 0 && selectedIds.length === products.length
  const someSelected = selectedIds.length > 0 && selectedIds.length < products.length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="loading-spinner" role="status">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="sr-only">{t('common.loading', 'Loading...')}</span>
      </div>
    )
  }

  if (products.length === 0) {
    return (
      <div className="card p-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          {t('products.empty.title', 'No products found')}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('products.empty.message', 'Import products from your feed to get started.')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Selection Header */}
      {selectable && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => el && (el.indeterminate = someSelected)}
              onChange={handleSelectAll}
              aria-label={t('products.selectAll', 'Select all')}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              {t('products.selectAll', 'Select all')}
            </span>
          </label>
          {selectedIds.length > 0 && (
            <span className="text-sm font-medium text-primary-600">
              {selectedIds.length} {t('products.selected', 'selected')}
            </span>
          )}
        </div>
      )}

      {/* Product Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {products.map((product) => (
          <ProductCard
            key={product.product_id}
            product={product}
            selectable={selectable}
            selected={selectedIds.includes(product.product_id)}
            onSelect={handleSelectProduct}
            onClick={onProductClick}
          />
        ))}
      </div>
    </div>
  )
}

ProductGrid.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    product_id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.number,
    currency: PropTypes.string,
    image_url: PropTypes.string,
    is_featured: PropTypes.bool,
    rating: PropTypes.number,
    rating_count: PropTypes.number,
    inventory_count: PropTypes.number,
    has_discount: PropTypes.bool,
    original_price: PropTypes.number,
  })),
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
  onProductClick: PropTypes.func,
  loading: PropTypes.bool,
}

export default ProductGrid
